import type { DocumentPageMetadataUserEditStatus } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import { documentPageMetadataUserEditStatusSchema } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { EvalPagePool } from 'features/knowledgeDocumentViewer/utils/pageMetadataEvalPool'
import type { MetadataEvalSampledPage } from 'features/knowledgeDocumentViewer/utils/pageMetadataEvalSampling'

export type EvalPageTrailDecision =
  | 'unlabeled'
  | DocumentPageMetadataUserEditStatus

export type EvalPageTrailItem = MetadataEvalSampledPage & {
  decision: EvalPageTrailDecision
}

export type EvalPageTrail = {
  visited: readonly EvalPageTrailItem[]
  cursor: number
}

export const EMPTY_EVAL_PAGE_TRAIL: EvalPageTrail = {
  visited: [],
  cursor: 0,
}

export function evalTrailDecisionForPool(
  pool: EvalPagePool
): EvalPageTrailDecision {
  if (pool === 'labeled_by_me') return 'confirmed'
  if (pool === 'deferred_by_me') return 'deferred'
  return 'unlabeled'
}

export function evalTrailDecisionForPage(
  pool: EvalPagePool,
  page: MetadataEvalSampledPage
): EvalPageTrailDecision {
  const parsed = documentPageMetadataUserEditStatusSchema.safeParse(
    page.label_status
  )
  if (parsed.success) return parsed.data
  return evalTrailDecisionForPool(pool)
}

function evalTrailItem(
  page: MetadataEvalSampledPage,
  decision: EvalPageTrailDecision
): EvalPageTrailItem {
  return { ...page, decision }
}

export function evalTrailCanGoBack(trail: EvalPageTrail): boolean {
  return trail.visited.length > 0 && trail.cursor > 0
}

export function evalTrailHasLaterPage(
  trail: EvalPageTrail,
  upcomingCount: number
): boolean {
  if (trail.visited.length === 0) return upcomingCount > 0
  return trail.cursor < trail.visited.length - 1 || upcomingCount > 0
}

export function evalQueueNavEnabled(args: {
  trail: EvalPageTrail
  upcomingCount: number
  pool: EvalPagePool
}): { canGoBack: boolean; canGoForward: boolean } {
  const canGoBack = evalTrailCanGoBack(args.trail)
  if (!evalTrailHasLaterPage(args.trail, args.upcomingCount)) {
    return { canGoBack, canGoForward: false }
  }
  if (args.pool !== 'not_labeled_by_me') {
    return { canGoBack, canGoForward: true }
  }
  const current = args.trail.visited[args.trail.cursor]
  const hasDecision =
    current?.decision === 'confirmed' || current?.decision === 'deferred'
  return { canGoBack, canGoForward: hasDecision }
}

export function evalTrailStepBack(
  trail: EvalPageTrail
): EvalPageTrail | undefined {
  if (!evalTrailCanGoBack(trail)) return undefined
  return { visited: trail.visited, cursor: trail.cursor - 1 }
}

export function evalTrailStepForwardVisited(
  trail: EvalPageTrail
): EvalPageTrail | undefined {
  if (trail.cursor >= trail.visited.length - 1) return undefined
  return { visited: trail.visited, cursor: trail.cursor + 1 }
}

export function evalTrailAppend(
  trail: EvalPageTrail,
  page: MetadataEvalSampledPage,
  decision: EvalPageTrailDecision
): EvalPageTrail {
  const existingIndex = trail.visited.findIndex(
    (item) => item.page_id === page.page_id
  )
  if (existingIndex >= 0) {
    return { visited: trail.visited, cursor: existingIndex }
  }
  const visited = [...trail.visited, evalTrailItem(page, decision)]
  return { visited, cursor: visited.length - 1 }
}

export function evalTrailMarkCursor(
  trail: EvalPageTrail,
  decision: DocumentPageMetadataUserEditStatus
): EvalPageTrail {
  const current = trail.visited[trail.cursor]
  if (current === undefined) return trail
  const visited = trail.visited.map((item, index) =>
    index === trail.cursor ? { ...item, decision } : item
  )
  return { visited, cursor: trail.cursor }
}

export function evalTrailPageAtCursor(
  trail: EvalPageTrail
): EvalPageTrailItem | undefined {
  return trail.visited[trail.cursor]
}

export function evalTrailVisitedPageIds(
  trail: EvalPageTrail
): readonly string[] {
  return trail.visited.map((page) => page.page_id)
}
